import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../utils/supabase';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';

export default function SuperAdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { updateAuth } = useSuperAdminAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Authenticate with Supabase (same as regular users)
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError || !authData.user) {
        throw new Error(authError?.message || 'Invalid credentials');
      }

      // Check if this user is a super admin by calling the API
      let API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      // Remove trailing slash if present
      if (API_BASE_URL.endsWith('/')) {
        API_BASE_URL = API_BASE_URL.slice(0, -1);
      }

      const response = await fetch(`${API_BASE_URL}/api/saas/me`, {
        headers: {
          'Authorization': `Bearer ${authData.session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // Sign out if not a super admin
        await supabase.auth.signOut();
        throw new Error('Access denied. Super admin access required.');
      }

      const adminData = await response.json();

      // Store session and admin info using the context to update state immediately
      updateAuth(authData.session.access_token, adminData.data);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries();

      // Navigate to management page
      navigate('/saas/manage');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-md w-full">
        <div className="bg-white p-10 rounded-[2.5rem] shadow-2xl shadow-zinc-200 border border-zinc-100">
          <div className="text-center mb-10">
            <div className="flex justify-center mb-10">
              <img src="/gympe.svg" alt="Gympe" className="h-10 w-auto" />
            </div>
            <div className="w-16 h-16 bg-zinc-950 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-xl shadow-zinc-200">
              <svg className="w-8 h-8 text-[#AAD816]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h2 className="text-3xl font-black text-zinc-950 uppercase tracking-tight">
              SaaS Admin
            </h2>
            <p className="mt-3 text-zinc-500 font-medium leading-relaxed">
              Sign in to manage the Gympe platform
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-2xl bg-red-50 border border-red-100 p-4 animate-shake">
                <div className="text-[10px] font-black uppercase tracking-tight text-red-600 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  {error}
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div className="space-y-1">
                <label htmlFor="email" className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 ml-1">
                  Admin Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="w-full px-5 py-4 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] transition-all duration-200 font-medium outline-none"
                  placeholder="admin@gympe.in"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="password" dangerouslySetInnerHTML={{ __html: 'Security Key' }} className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 ml-1" />
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="w-full px-5 py-4 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] transition-all duration-200 font-medium outline-none"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-zinc-950 hover:bg-zinc-900 text-white font-black uppercase tracking-widest py-4 px-4 rounded-2xl transition-all active:scale-[0.98] shadow-lg shadow-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-[#AAD816] border-t-transparent rounded-full animate-spin mr-3"></div>
                    Authenticating...
                  </div>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Access Console
                    <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

